import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { apiInstance } from "@shared/services";
import { convertObjectToCamelCase, handleApiError } from "@shared/helpers";

import { PAYFAC_QUERIES } from "./queries";

import type { ISignedDashboard } from "@payfac/types";

const METABASE_ORIGIN = "https://external-analytics.barte.com";

const URLS = {
  signedDashboard: `v2/report/signed-dashboard`,
};

const api = apiInstance({});

export type SignedDashboardName =
  | "payfac-transactions"
  | "payfac-home"
  | "terminals"
  | "commissions";

export const useGetReportSignedDashboard = (
  dashboardName: SignedDashboardName,
  idCompany: number,
) => {
  const { t } = useTranslation();

  const getReportSignedDashboard = async (): Promise<{
    success: boolean;
    message: string;
    data?: ISignedDashboard;
  }> => {
    try {
      const { data } = await api.get(`${URLS.signedDashboard}/${dashboardName}`, {
        params: { idCompany },
      });

      const parsedUrl = new URL(data.url);
      if (parsedUrl.origin !== METABASE_ORIGIN) {
        return {
          success: false,
          message: t("payfac.pageAnalyticsServiceError"),
        };
      }

      return {
        success: true,
        message: t("payfac.pageAnalyticsServiceSuccess"),
        data: convertObjectToCamelCase(data) as ISignedDashboard,
      };
    } catch (error: any) {
      return handleApiError(error, t("payfac.pageAnalyticsServiceError"));
    }
  };

  return useQuery({
    queryKey: [PAYFAC_QUERIES.SIGNED_DASHBOARD, dashboardName, idCompany],
    queryFn: () => getReportSignedDashboard(),
    enabled: !!idCompany,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });
};
